import { optimizedImageSrc } from "@/lib/imageVariants";
import ResponsiveImage from "@/components/ResponsiveImage";
import { useRef, useState, useEffect } from "react";

interface LazyVideoProps {
  src: string;
  poster?: string;
  className?: string;
}

/**
 * LazyVideo: Only loads the video source when the element enters the viewport.
 * This prevents the browser from downloading large video files on initial page load.
 * Shows the poster image until the user scrolls to the video section.
 */
export default function LazyVideo({ src, poster, className }: LazyVideoProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" } // Start loading 200px before it's visible
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef}>
      {isVisible ? (
        <video
          src={src}
          controls
          preload="metadata"
          playsInline
          poster={poster ? optimizedImageSrc(poster, 960) : undefined}
          className={className}
        />
      ) : (
        <div className={`relative ${className || ""}`}>
          {poster && (
            <ResponsiveImage sizes="(min-width: 1280px) 584px, (min-width: 768px) calc((100vw - 80px) / 2), calc(100vw - 32px)"
              src={poster}
              alt="Video förhandsvisning"
              className="w-full h-full object-cover"
              loading="lazy"
              width={1600}
              height={900}
            />
          )}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
              <svg
                className="w-6 h-6 text-navy ml-1"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
